import { useState, FormEvent } from 'react';

interface BookingModalProps {
  onClose: () => void;
}

export default function BookingModal({ onClose }: BookingModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5l5es5porqtj2nscjb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', industry: '', message: '' });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-midnight-navy/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        <div className="p-6 border-b border-soft-silver flex items-center justify-between">
          <h3 className="text-2xl font-serif font-bold text-midnight-navy">Book a Strategy Call</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-gray hover:text-midnight-navy transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-readdy-form id="strategy-call-booking">
          <div>
            <label className="block text-sm font-medium text-midnight-navy mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-soft-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-wealth-gold text-sm text-midnight-navy"
              placeholder="John Smith"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-midnight-navy mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-soft-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-wealth-gold text-sm text-midnight-navy"
                placeholder="john@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-midnight-navy mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-soft-silver rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight-navy mb-2">
              Company Name
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full px-4 py-3 border border-soft-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-wealth-gold text-sm text-midnight-navy"
              placeholder="Your Company"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight-navy mb-2">
              Industry
            </label>
            <select
              name="industry"
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              className="w-full px-4 py-3 border border-soft-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-wealth-gold text-sm text-midnight-navy cursor-pointer"
            >
              <option value="">Select an industry</option>
              <option value="home-services">Home Services</option>
              <option value="health-wellness">Health & Wellness</option>
              <option value="real-estate">Real Estate & Insurance</option>
              <option value="legal">Legal Services</option>
              <option value="financial">Financial Services</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight-navy mb-2">
              What challenges are you looking to solve?
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 border border-soft-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-wealth-gold text-sm text-midnight-navy resize-none"
              placeholder="Tell us about your operational challenges..."
            />
            <p className="text-xs text-slate-gray mt-1">{formData.message.length}/500 characters</p>
          </div>

          {submitStatus === 'success' && (
            <div className="p-4 bg-emerald-insight/10 border border-emerald-insight rounded-lg">
              <p className="text-sm text-emerald-insight font-medium">
                Thank you! We'll be in touch within 24 hours.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 font-medium">
                Something went wrong. Please try again.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-4 bg-wealth-gold text-midnight-navy text-base font-semibold rounded-lg hover:bg-wealth-gold/90 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Schedule Call'}
          </button>
        </form>
      </div>
    </div>
  );
}
